import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { authenticatedDownload } from "../lib/pdf";
import { fmtUSDate } from "../lib/date";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { FileText, Upload, Trash2, Download, Pencil, X, Tag } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["general", "lease", "id", "insurance", "w9", "receipt", "correspondence", "other"];
const MAX_MB = 15;

function sizeKB(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export default function TenantDocumentsDialog({ tenant, open, onOpenChange }) {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState("");
  const [category, setCategory] = useState("general");
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [editCategory, setEditCategory] = useState("general");

  const load = async () => {
    if (!tenant) return;
    try {
      const { data } = await api.get(`/tenants/${tenant.id}/documents`);
      setDocs(data);
    } catch (error) {
      console.error("Failed to load documents", error);
    }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, tenant?.id]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`File too large — max ${MAX_MB}MB`);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", label || file.name);
      fd.append("category", category);
      await api.post(`/tenants/${tenant.id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("File uploaded");
      setLabel("");
      e.target.value = "";
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  };

  const download = async (d) => {
    try {
      await authenticatedDownload(
        `/api/tenants/${tenant.id}/documents/${d.id}/download`,
        d.original_filename || d.label || "document",
      );
    } catch (error) {
      console.error("Document download failed", error);
      toast.error("Download failed");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this document?")) return;
    await api.delete(`/tenants/${tenant.id}/documents/${id}`);
    load();
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/tenants/${tenant.id}/documents/${editingId}`, {
        label: editLabel,
        category: editCategory,
      });
      toast.success("Label updated");
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  if (!tenant) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle style={{fontFamily:"Outfit"}}>Documents — {tenant.full_name}</DialogTitle>
        </DialogHeader>

        {/* Upload row */}
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 mb-3">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
            <label className="md:col-span-3 block">
              <div className="overline text-slate-600 mb-1 flex items-center gap-1.5"><Tag className="w-3 h-3" /> Label</div>
              <input
                className="input-clean w-full"
                data-testid="doc-label-input"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Signed lease 2026 · Insurance COI"
                maxLength={120}
              />
            </label>
            <label className="md:col-span-2 block">
              <div className="overline text-slate-600 mb-1">Category</div>
              <select className="input-clean w-full" data-testid="doc-category-select" value={category} onChange={(e) => setCategory(e.target.value)}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          </div>
          <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs text-slate-500 font-mono">Max {MAX_MB}MB — PDF, images, docs.</div>
            <label className={`btn-primary-dark flex items-center gap-2 text-sm cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`}>
              <Upload className="w-4 h-4" /> {busy ? "Uploading…" : "Upload file"}
              <input
                type="file"
                data-testid="doc-file-input"
                className="hidden"
                onChange={onFile}
                disabled={busy}
                accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx,.xls,.xlsx,.csv,.txt"
              />
            </label>
          </div>
        </div>

        {/* File list */}
        <div className="border border-slate-200 rounded-sm overflow-hidden overflow-y-auto max-h-[48vh]">
          {docs.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-400" data-testid="no-documents">
              No documents uploaded yet.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="documents-list">
              {docs.map((d) => (
                <li key={d.id} className="p-3 flex items-center gap-3 hover:bg-slate-50 transition-colors" data-testid={`document-${d.id}`}>
                  <div className="w-9 h-9 bg-slate-900 text-white flex items-center justify-center rounded-sm shrink-0"><FileText className="w-4 h-4" /></div>
                  <div className="min-w-0 flex-1">
                    {editingId === d.id ? (
                      <div className="flex items-center gap-2">
                        <input className="input-clean flex-1" data-testid={`edit-label-${d.id}`} value={editLabel} onChange={(e) => setEditLabel(e.target.value)} maxLength={120} />
                        <select className="input-clean" data-testid={`edit-category-${d.id}`} value={editCategory} onChange={(e) => setEditCategory(e.target.value)}>
                          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <button onClick={saveEdit} className="btn-primary-dark text-xs px-3 py-1" data-testid={`save-label-${d.id}`}>Save</button>
                        <button onClick={() => setEditingId(null)} className="text-slate-400 hover:text-slate-900 p-1"><X className="w-4 h-4" /></button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900 truncate">{d.label || d.original_filename}</span>
                          <span className="overline text-slate-500 bg-slate-100 px-2 py-0.5 rounded-sm text-[10px]">{d.category}</span>
                        </div>
                        <div className="text-xs text-slate-500 font-mono mt-0.5 truncate">
                          {d.original_filename} · {sizeKB(d.size)} · {fmtUSDate(d.created_at)}
                        </div>
                      </>
                    )}
                  </div>
                  {editingId !== d.id && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => download(d)} data-testid={`download-doc-${d.id}`} className="text-slate-500 hover:text-slate-900 p-1.5" title="Download"><Download className="w-4 h-4" /></button>
                      <button onClick={() => { setEditingId(d.id); setEditLabel(d.label || ""); setEditCategory(d.category || "general"); }} data-testid={`edit-doc-${d.id}`} className="text-slate-500 hover:text-slate-900 p-1.5" title="Edit label"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(d.id)} data-testid={`delete-doc-${d.id}`} className="text-slate-500 hover:text-red-600 p-1.5" title="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
